import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { MapPin, Mail, Phone, MessageSquare, Clock, AlertCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import companyInfo from '../data/companyInfo.json';
import { CREATE_CONTACT_MESSAGE } from '../graphql/mutations/createContactMessage';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [createContactMessage, { loading: isLoading }] = useMutation(CREATE_CONTACT_MESSAGE);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate email
    if (!validateEmail(formData.email)) {
      setError('Indtast venligst en gyldig email-adresse.');
      return;
    }

    try {
      const { data } = await createContactMessage({
        variables: {
          input: {
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
          },
        },
      });

      if (data?.createContactMessage?.contactMessage) {
        setSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else if (data?.createContactMessage?.errors) {
        setError(data.createContactMessage.errors);
      } else {
        setError('Kunne ikke sende besked. Prøv venligst igen.');
      }
    } catch (err) {
      setError('Kunne ikke sende besked. Prøv venligst igen.');
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="h-6 w-6" />,
      title: 'Besøg os',
      details: [companyInfo.name, companyInfo.address.street, `${companyInfo.address.postalCode} ${companyInfo.address.city}`, companyInfo.address.country],
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: 'Email os',
      details: [companyInfo.contact.email.general, companyInfo.contact.email.support],
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Ring til os',
      details: [companyInfo.contact.phone, 'Man-Tor 9-15'],
    },
  ];

  const faqs = [
    {
      question: 'Hvor hurtigt kan jeg forvente et svar?',
      answer: 'Vi sigter mod at svare på alle henvendelser inden for 24 timer på hverdage.',
    },
    {
      question: 'Tilbyder I telefonsupport?',
      answer: 'Ja, vores telefonsupport er tilgængelig mandag til torsdag, 9-15.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Kontakt os</h1>
          <p className="text-xl text-gray-600">
            Har du spørgsmål eller vil du vide mere om DoMore? Vi er her for at hjælpe.
          </p>
        </div>

        {/* Contact Information Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
          {contactInfo.map((info) => (
            <div key={info.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-green-600 mb-4">{info.icon}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{info.title}</h3>
              <div className="space-y-1">
                {info.details.map((detail, index) => (
                  <p key={index} className="text-gray-600">{detail}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send os en besked</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Dit navn
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email-adresse
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Emne
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Besked
                  </label>
                  <textarea
                    id="message"
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    required
                  />
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">{error}</h3>
                      </div>
                    </div>
                  </div>
                )}

                {success && (
                  <div className="rounded-md bg-green-50 p-4">
                    <div className="flex">
                      <MessageSquare className="h-5 w-5 text-green-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Besked sendt! Vi vender tilbage hurtigst muligt.
                        </h3>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  isLoading={isLoading}
                >
                  Send besked
                </Button>
              </form>
            </div>

            {/* FAQ and Support Hours */}
            <div className="space-y-8">
              {/* Support Hours */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center text-green-600 mb-4">
                  <Clock className="h-6 w-6 mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">Supporttider</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Kundesupport</h3>
                    <p className="text-gray-600">{companyInfo.businessHours.customerSupport.weekdays}</p>
                    <p className="text-gray-600">{companyInfo.businessHours.customerSupport.saturday}</p>
                    <p className="text-gray-600">{companyInfo.businessHours.customerSupport.sunday}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Teknisk support</h3>
                    <p className="text-gray-600">{companyInfo.businessHours.technicalSupport.weekdays}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      For hastesager uden for åbningstid, send venligst en email til<br />
                      <a href={`mailto:${companyInfo.contact.email.support}`} className="text-green-600 underline">{companyInfo.contact.email.support}</a>, så vender vi tilbage hurtigst muligt.
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQs */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center text-green-600 mb-4">
                  <MessageSquare className="h-6 w-6 mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">Ofte stillede spørgsmål</h2>
                </div>
                <div className="space-y-6">
                  {faqs.map((faq, index) => (
                    <div key={index}>
                      <h3 className="font-medium text-gray-900 mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;